// File: frontend/src/components/online/OnlineUsersModal.tsx
// src/components/online/OnlineUsersModal.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import "./OnlineUsersModal.css";
import {
  OnlinePresenceStatus,
  OnlineStats,
  OnlineUserItem,
  PRESENCE_STATUS_LIST,
  getOnlineStats,
  getOnlineUsers,
  getPresenceMeta,
  updatePresenceStatus,
} from "../../services/onlineUsersService";
import { onOnlinePresenceChanged, onPresenceStatusChange } from "../../services/websocketService";
import { generateProfileUrl } from "../../utils/slugGenerator";
import { useAuth } from "../../context/AuthContext";
import { formatCompactWithTitle } from "../../utils";

// ─── Role helpers ─────────────────────────────────────────────────────────────

const useRoleLabel = () => {
  const { t } = useTranslation();
  return (role: string): string => {
    switch (role.toUpperCase()) {
      case "ADMIN":     return t("status_admin");
      case "MODERATOR": return t("status_moderator");
      case "AUTHOR":    return t("status_author");
      case "ROOKIE":    return t("status_rookie");
      default:          return role;
    }
  };
};

/**
 * Role power order used to sort the list: admin → moderator → author → rookie.
 * Unknown roles sink to the bottom.
 */
const ROLE_RANK: Record<string, number> = {
  ADMIN: 0,
  MODERATOR: 1,
  AUTHOR: 2,
  ROOKIE: 3,
};

const roleRank = (role: string): number => ROLE_RANK[role.toUpperCase()] ?? 99;

/** i18n key of the one-letter role badge (TR: A / M / Y / Ç). */
const ROLE_LETTER_KEY: Record<string, string> = {
  ADMIN: "online_role_letter_admin",
  MODERATOR: "online_role_letter_moderator",
  AUTHOR: "online_role_letter_author",
  ROOKIE: "online_role_letter_rookie",
};

/**
 * Sort by role power first, then by username (Turkish collation) so the list
 * keeps a stable, predictable order regardless of the backend's ordering.
 */
const sortByRoleThenName = (a: OnlineUserItem, b: OnlineUserItem): number =>
  roleRank(a.role) - roleRank(b.role) || a.username.localeCompare(b.username, "tr");

// ─── Icons ────────────────────────────────────────────────────────────────────

const MessageIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
  </svg>
);

const DefaultAvatarIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

const ChevronIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
    <path d="M6 9l6 6 6-6"/>
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
    <path d="M5 12l5 5 9-10"/>
  </svg>
);

// ─── Presence dot indicator ───────────────────────────────────────────────────

interface PresenceDotProps {
  status: OnlinePresenceStatus;
  /** Tooltip text (status name). Omitted for purely decorative dots. */
  title?: string;
}

/**
 * Colored status dot. The tooltip replaces the old status emoji / text so the
 * row stays compact. INVISIBLE renders as a dashed ring (only MOD/ADMIN and the
 * user themselves ever receive INVISIBLE rows from the backend).
 */
const PresenceDot: React.FC<PresenceDotProps> = ({ status, title }) => {
  const meta = getPresenceMeta(status);
  const isInvisible = status === "INVISIBLE";
  const className = `online-presence-dot${isInvisible ? " online-presence-dot--invisible" : ""}`;

  return (
    <span
      className={className}
      style={isInvisible ? undefined : { backgroundColor: meta.color }}
      title={title}
      aria-label={title}
      role={title ? "img" : undefined}
    />
  );
};

// ─── Avatar with broken-image fallback ────────────────────────────────────────

interface AvatarProps {
  url: string | null;
}

/**
 * Small profile avatar. Falls back to the default icon when there is no URL or
 * the image fails to load — previously a missing media file rendered the
 * browser's broken-image icon with the alt text inside the circle.
 */
const Avatar: React.FC<AvatarProps> = ({ url }) => {
  const [failed, setFailed] = useState(false);

  // A new URL (e.g. after the user uploads an avatar) deserves a fresh attempt
  useEffect(() => { setFailed(false); }, [url]);

  return (
    <span className="online-user-row__avatar" aria-hidden="true">
      {url && !failed ? (
        <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <DefaultAvatarIcon />
      )}
    </span>
  );
};

// ─── User row ─────────────────────────────────────────────────────────────────

interface UserRowProps {
  user: OnlineUserItem;
  currentUserId: number | null;
  currentUserRole: string | null;
  onClose: () => void;
}

const UserRow: React.FC<UserRowProps> = ({ user, currentUserId, currentUserRole, onClose }) => {
  const navigate     = useNavigate();
  const { t }        = useTranslation();
  const getRoleLabel = useRoleLabel();

  const handleProfileClick = () => {
    onClose();
    navigate(generateProfileUrl(user.username, user.id));
  };

  // Navigate to messages page and pass recipient info via location state
  // so MessagesPage can immediately open a new conversation with this user.
  const handleMessageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    navigate("/mesajlar", {
      state: {
        recipientId:       user.id,
        recipientUsername: user.username,
        recipientRole:     user.role,
      },
    });
  };

  // Do not show message button for the current user's own row
  const isSelf = currentUserId !== null && currentUserId === user.id;

  // Role hierarchy for AUTHORS privacy check
  const AUTHOR_AND_ABOVE = ["AUTHOR", "MODERATOR", "ADMIN"];
  const viewerIsAuthorOrAbove = currentUserRole
    ? AUTHOR_AND_ABOVE.includes(currentUserRole.toUpperCase())
    : false;

  // Determine if the message button should be shown:
  // - NOBODY:    never show (user blocked all messages)
  // - AUTHORS:   only show if the current viewer is AUTHOR, MODERATOR, or ADMIN
  // - FOLLOWERS: show (can't check follow status here; backend will reject if not following)
  // - EVERYONE:  always show
  const canMessage = !isSelf && (() => {
    switch (user.messagePrivacy) {
      case "NOBODY":   return false;
      case "AUTHORS":  return viewerIsAuthorOrAbove;
      default:         return true; // EVERYONE, FOLLOWERS
    }
  })();

  const presenceStatus = user.presenceStatus ?? "ONLINE";
  const meta           = getPresenceMeta(presenceStatus);
  const roleKey        = user.role.toUpperCase();
  const roleLabel      = getRoleLabel(user.role);
  const roleLetterKey  = ROLE_LETTER_KEY[roleKey];

  return (
    <div className="online-user-row">
      {/* Role badge — one letter, full role name as tooltip */}
      <span
        className={`online-user-row__role-badge role-${roleKey}`}
        title={roleLabel}
        aria-label={roleLabel}
        role="img"
      >
        {roleLetterKey ? t(roleLetterKey) : roleKey.charAt(0)}
      </span>

      {/* Clickable profile area: avatar + username */}
      <button
        type="button"
        className="online-user-row__profile"
        onClick={handleProfileClick}
        title={user.username}
      >
        <Avatar url={user.avatarUrl} />
        {/* No "@" prefix — the list only contains authors, the prefix was noise */}
        <span className="online-user-row__username">{user.username}</span>
      </button>

      {/* Message button — revealed on row hover / focus; always visible on touch.
          The placeholder keeps the status dots in one column when it is absent. */}
      {canMessage ? (
        <button
          type="button"
          className="online-user-row__message-btn"
          onClick={handleMessageClick}
          aria-label={t("online_send_message", { username: user.username })}
          title={t("online_send_message", { username: user.username })}
        >
          <MessageIcon />
        </button>
      ) : (
        <span className="online-user-row__message-placeholder" />
      )}

      {/* Presence status as a colored dot; the status name is the tooltip */}
      <PresenceDot status={presenceStatus} title={t(meta.labelKey)} />
    </div>
  );
};

// ─── Own presence status selector ─────────────────────────────────────────────

interface PresenceSelectProps {
  currentStatus: OnlinePresenceStatus;
  onChange: (status: OnlinePresenceStatus) => void;
  saving: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Single button showing the current status; opens a labelled list of all
 * statuses. Replaces the row of seven unlabelled emoji buttons.
 */
const PresenceSelect: React.FC<PresenceSelectProps> = ({
  currentStatus, onChange, saving, open, onOpenChange,
}) => {
  const { t }   = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const current = getPresenceMeta(currentStatus);

  // Close the list on a click outside the selector
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, onOpenChange]);

  return (
    <div className="online-presence-select" ref={wrapRef}>
      <button
        type="button"
        className={`online-presence-select__button${open ? " open" : ""}`}
        onClick={() => onOpenChange(!open)}
        disabled={saving}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <PresenceDot status={currentStatus} />
        <span className="online-presence-select__label">{t(current.labelKey)}</span>
        <ChevronIcon />
      </button>

      {open && (
        <div className="online-presence-select__menu" role="listbox" aria-label={t("presence_your_status")}>
          {PRESENCE_STATUS_LIST.map((meta) => {
            const selected = meta.status === currentStatus;
            return (
              <button
                key={meta.status}
                type="button"
                role="option"
                aria-selected={selected}
                className={`online-presence-select__option${selected ? " selected" : ""}`}
                onClick={() => {
                  onOpenChange(false);
                  onChange(meta.status);
                }}
              >
                <PresenceDot status={meta.status} />
                <span className="online-presence-select__option-text">
                  <span>{t(meta.labelKey)}</span>
                  {meta.status === "INVISIBLE" && (
                    <small>{t("presence_invisible_desc")}</small>
                  )}
                </span>
                {selected && <span className="online-presence-select__check"><CheckIcon /></span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Visitor stats bar ────────────────────────────────────────────────────────

interface StatsBarProps {
  stats: OnlineStats;
}

const StatsBar: React.FC<StatsBarProps> = ({ stats }) => {
  const { t } = useTranslation();

  const items = [
    { value: stats.today,     tooltipKey: "online_stats_today_tip", labelKey: "online_stats_today_short" },
    { value: stats.thisWeek,  tooltipKey: "online_stats_week_tip",  labelKey: "online_stats_7d"          },
    { value: stats.thisMonth, tooltipKey: "online_stats_month_tip", labelKey: "online_stats_30d"         },
    { value: stats.thisYear,  tooltipKey: "online_stats_year_tip",  labelKey: "online_stats_this_year"   },
    // All-time member total — moved from the removed heading row into the boxes
    { value: stats.total,     tooltipKey: "online_stats_total_tip", labelKey: "online_stats_total_short" },
  ];

  // Large values are shortened ("1,2k", "3,4M"); the exact value is appended
  // to the tooltip whenever the displayed text is shortened.
  const withExact = (tip: string, exact?: string): string =>
    exact ? `${tip} (${exact})` : tip;

  return (
    <div className="online-stats">
      <div className="online-stats-bar">
        {items.map((item, idx) => {
          const value = formatCompactWithTitle(item.value);
          return (
            <div
              key={idx}
              className="online-stats-bar__item"
              title={withExact(t(item.tooltipKey), value.title)}
            >
              <span className="online-stats-bar__value">{value.text}</span>
              <span className="online-stats-bar__label">{t(item.labelKey)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

interface OnlineUsersModalProps {
  /** Ref of the trigger button — modal anchors below it, aligned to the right */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

export const OnlineUsersModal: React.FC<OnlineUsersModalProps> = ({ triggerRef, onClose }) => {
  const { t }    = useTranslation();
  const { user } = useAuth();

  const [users,   setUsers]   = useState<OnlineUserItem[]>([]);
  const [stats,   setStats]   = useState<OnlineStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  const [search,  setSearch]  = useState("");

  // Current user's own presence status (initialised from list, then kept in sync)
  const [myStatus,     setMyStatus]     = useState<OnlinePresenceStatus>("ONLINE");
  const [savingStatus, setSavingStatus] = useState(false);
  // Own-status list open state — lifted here so Escape closes the list first
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  // Position of the modal panel (anchored below the trigger button)
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);

  // Calculate position from trigger button rect
  useEffect(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top:   rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, [triggerRef]);

  // Recompute on resize
  useEffect(() => {
    const recalc = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [triggerRef]);

  const REFRESH_INTERVAL_MS = 30_000;

  const fetchData = useCallback(async () => {
    try {
      // Fetch user list and stats in parallel
      const [data, statsData] = await Promise.all([
        getOnlineUsers(),
        getOnlineStats(),
      ]);
      setUsers(data);
      setStats(statsData);
      setError(false);

      // Sync the current user's own status from the list
      if (user) {
        const self = data.find((u) => u.id === user.id);
        if (self) setMyStatus(self.presenceStatus ?? "ONLINE");
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch + periodic refresh
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Re-fetch as soon as somebody comes online or goes offline, instead of waiting
  // up to 30s for the next poll. Debounced so a burst of connects (e.g. right
  // after a deploy, when every client reconnects) causes a single request.
  const ONLINE_CHANGE_DEBOUNCE_MS = 1_000;
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = onOnlinePresenceChanged(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fetchData();
      }, ONLINE_CHANGE_DEBOUNCE_MS);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [fetchData]);

  // Subscribe to real-time presence status broadcasts from WebSocket
  // so the list row updates immediately when any user changes their status
  // without waiting for the 30-second polling interval.
  useEffect(() => {
    // Capture current user id and role at subscription time.
    // Using a ref would be cleaner for long-lived effects, but capturing here
    // is sufficient because the effect re-runs whenever `user` changes (login/logout).
    const currentUserId = user?.id ?? null;
    const currentUserRole = (user?.role ?? "").toUpperCase();
    const isModOrAdmin = ["MODERATOR", "ADMIN"].includes(currentUserRole);

    const unsubscribe = onPresenceStatusChange((event) => {
      const newStatus = event.status as OnlinePresenceStatus;
      const isEventFromSelf = event.userId === currentUserId;

      setUsers((prev) => {
        const userAlreadyInList = prev.some((u) => u.id === event.userId);

        // A previously-invisible user is becoming visible again but is not in our local
        // list — we cannot reconstruct their full row from the event payload alone.
        // Re-fetch the list from the backend so their row reappears correctly.
        if (!userAlreadyInList && newStatus !== "INVISIBLE") {
          Promise.resolve().then(() => fetchData());
          return prev; // leave the list unchanged until fetchData() resolves
        }

        return prev
          .map((u) =>
            u.id === event.userId ? { ...u, presenceStatus: newStatus } : u
          )
          // If the user switched to INVISIBLE:
          // - Regular viewers: remove the row immediately (mirrors backend filter)
          // - MOD/ADMIN: keep the row with a ghost dot
          // - The user themselves: always keep their own row so they can switch back
          .filter((u) => {
            if (u.id === event.userId && newStatus === "INVISIBLE") {
              if (isModOrAdmin || isEventFromSelf) return true;
              return false;
            }
            return true;
          });
      });

      // Keep the picker in sync when the event is the current user's own status change
      if (isEventFromSelf) {
        setMyStatus(newStatus);
      }
    });
    return unsubscribe;
  }, [user, fetchData]);

  // Handle own presence status change from the picker
  const handlePresenceChange = async (status: OnlinePresenceStatus) => {
    if (status === myStatus || savingStatus) return;
    setSavingStatus(true);
    try {
      await updatePresenceStatus(status);
      setMyStatus(status);
      // The WebSocket broadcast will update the list row for all viewers
    } catch {
      // Silently fail — the old status remains selected
    } finally {
      setSavingStatus(false);
    }
  };

  // Close on Escape key or click outside the panel
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // An open status list closes first; a second Escape closes the modal
      if (statusMenuOpen) {
        setStatusMenuOpen(false);
        return;
      }
      onClose();
    };
    const handleClick = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [onClose, triggerRef, statusMenuOpen]);

  // Search (Turkish-aware) + role-power ordering. Role filter tabs were removed:
  // the role badge and the fixed admin → moderator → author → rookie order make
  // the roles readable at a glance without an extra control row.
  const searchTrim    = search.trim().toLocaleLowerCase("tr");
  const filteredUsers = users
    .filter(u => !searchTrim || u.username.toLocaleLowerCase("tr").includes(searchTrim))
    .sort(sortByRoleThenName);

  if (!pos) return null;

  const isAuthenticated = !!user;

  return ReactDOM.createPortal(
    <div
      ref={panelRef}
      className="online-users-modal"
      style={{ top: pos.top, right: pos.right }}
      role="dialog"
      aria-modal="true"
      aria-label={t("online_users_title")}
    >
      {/* Header */}
      <div className="online-users-modal__header">
        <div className="online-users-modal__title-group">
          <span className="online-users-modal__live-dot" aria-hidden="true" />
          <h2 className="online-users-modal__title">{t("online_users_title")}</h2>
          {/* Total online count — not the search-filtered count */}
          {!loading && !error && (
            <span className="online-users-modal__count">{users.length}</span>
          )}
        </div>
        <button
          className="online-users-modal__close"
          onClick={onClose}
          aria-label={t("close")}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      {/* Presence status selector — only shown to authenticated users */}
      {isAuthenticated && (
        <div className="online-users-modal__presence-bar">
          <span className="online-users-modal__presence-label">{t("presence_your_status")}</span>
          <PresenceSelect
            currentStatus={myStatus}
            onChange={handlePresenceChange}
            saving={savingStatus}
            open={statusMenuOpen}
            onOpenChange={setStatusMenuOpen}
          />
        </div>
      )}

      {/* Search box — always visible */}
      <div className="online-users-modal__search">
        <label className="online-users-modal__search-box">
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" className="online-users-modal__search-icon" aria-hidden="true">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            className="online-users-modal__search-input"
            type="text"
            placeholder={t("online_search_placeholder")}
            aria-label={t("online_search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {search && (
            <button
              type="button"
              className="online-users-modal__search-clear"
              onClick={() => setSearch("")}
              aria-label={t("online_search_clear")}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          )}
        </label>
      </div>

      {/* Scrollable user list */}
      <div className="online-users-modal__list">
        {loading && (
          <div className="online-users-modal__state">
            <div className="online-users-spinner" />
          </div>
        )}

        {!loading && error && (
          <div className="online-users-modal__state">
            <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <span>{t("online_users_error")}</span>
          </div>
        )}

        {!loading && !error && filteredUsers.length === 0 && (
          <div className="online-users-modal__state">
            <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
            <span>
              {searchTrim
                ? t("online_users_empty_search")
                : t("online_users_empty")}
            </span>
          </div>
        )}

        {!loading && !error && filteredUsers.map(u => (
          <UserRow
            key={u.id}
            user={u}
            currentUserId={user?.id ?? null}
            currentUserRole={user?.role ?? null}
            onClose={onClose}
          />
        ))}
      </div>

      {/* Footer: visitor stats bar */}
      {!loading && !error && stats && (
        <div className="online-users-modal__footer">
          <StatsBar stats={stats} />
        </div>
      )}
    </div>,
    document.body
  );
};
